import React, { useState, useRef } from 'react';
import AddressForm from './components/AddressForm';
import RouteMap from './components/RouteMap';
import logo from './assets/logo.svg';
import FeedbackForm from './components/FeedbackForm';

interface Address {
  id: string;
  text: string;
  coordinates: [number, number];
}

const calculateDistance = (coord1: [number, number], coord2: [number, number]): number => {
  const R = 3959; // Earth's radius in miles
  const lat1 = coord1[1] * Math.PI / 180;
  const lat2 = coord2[1] * Math.PI / 180;
  const deltaLat = (coord2[1] - coord1[1]) * Math.PI / 180;
  const deltaLon = (coord2[0] - coord1[0]) * Math.PI / 180;

  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const findNearestNeighborRoute = (startLocation: Address, locations: Address[]): Address[] => {
  // If no locations, return empty route
  if (locations.length === 0) return [];

  // Initialize variables
  const unvisited = new Set(locations.map((_, index) => index));
  const route: Address[] = [];
  let currentCoords = startLocation.coordinates;

  // Continue until all locations are visited
  while (unvisited.size > 0) {
    // Find the nearest unvisited location
    let nearestIndex = -1;
    let shortestDistance = Infinity;

    unvisited.forEach(index => {
      const distance = calculateDistance(currentCoords, locations[index].coordinates);
      if (distance < shortestDistance) {
        shortestDistance = distance;
        nearestIndex = index;
      }
    });

    // Add nearest location to route and update current position
    route.push(locations[nearestIndex]);
    unvisited.delete(nearestIndex);
    currentCoords = locations[nearestIndex].coordinates;
  }

  return route;
};

const App: React.FC = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [optimizedAddresses, setOptimizedAddresses] = useState<Address[]>([]);
  const [showMap, setShowMap] = useState(false);
  const [mapOverlayOpen, setMapOverlayOpen] = useState(false);
  const [hasShownOverlay, setHasShownOverlay] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  const handleAddressesChange = (newAddresses: Address[]) => {
    setAddresses(newAddresses);
  };

  const handleOptimizeRoute = () => {
    // Get the starting location (first address) and remaining locations
    const [startLocation, ...remainingLocations] = addresses;
    
    // Calculate the optimized route
    const optimizedRoute = [startLocation, ...findNearestNeighborRoute(startLocation, remainingLocations)];
    setOptimizedAddresses(optimizedRoute);
    setShowMap(true);
    if (!hasShownOverlay) {
      setMapOverlayOpen(true);
      setHasShownOverlay(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <header className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-4">
            <img 
              src={logo} 
              alt="Agent Boston Logo" 
              className="w-12 h-12 object-contain"
              style={{ filter: 'brightness(1) contrast(1)' }}
            />
            <h1 className="text-4xl font-bold text-white">
              Agent Akshay
            </h1>
          </div>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Enter the addresses of your property showings, and I'll optimize the route to save time and maximize efficiency.
          </p>
        </header>

        <div className="flex flex-col items-center max-w-4xl mx-auto ">
          <div className="w-full space-y-8 z-1">
            <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
              <h2 className="text-xl font-semibold mb-6 text-center">Enter your Showing Addresses</h2>
              <AddressForm
                onAddressesChange={handleAddressesChange}
                onOptimizeRoute={handleOptimizeRoute}
              />
            </div>

            {showMap && optimizedAddresses.length > 1 && (
              <>
                <div ref={mapRef} className="bg-gray-800 rounded-xl p-6 shadow-lg">
                  <h2 className="text-xl font-semibold mb-6 text-center">Optimized Route</h2>
                  <RouteMap addresses={optimizedAddresses} />
                  <div className="mt-4 text-center text-sm text-gray-400">
                    Route starts from your location and shows the closest properties in order
                  </div>
                </div>
                <button
                  className="mt-4 mx-auto block px-6 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-all duration-200 transform hover:scale-105"
                  onClick={() => setMapOverlayOpen(true)}
                >
                  View Optimized Route Map
                </button>
              </>
            )}
          </div>
        </div>

        <footer className="mt-16 text-center text-gray-400 text-sm flex flex-col items-center gap-4">
          <p>© {new Date().getFullYear()} Agent Akshay. All rights reserved.</p>
          <button
            className="px-6 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold mt-2"
            onClick={() => setFeedbackOpen(true)}
          >
            Send Your Feedback
          </button>
        </footer>
      </div>

      {/* Responsive Map Overlay Modal */}
      {mapOverlayOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl h-[80vh] mx-2 bg-gray-900 rounded-2xl shadow-2xl flex flex-col">
            <button
              className="absolute top-4 right-4 z-10 bg-gray-700 hover:bg-gray-600 text-white rounded-full p-2 shadow-lg"
              onClick={() => setMapOverlayOpen(false)}
              aria-label="Close map overlay"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-xl font-semibold mb-2 text-center pt-6 text-white">Optimized Route</h2>
            <div className="flex-1 min-h-0">
              <RouteMap addresses={optimizedAddresses} />
            </div>
            <div className="mt-2 text-center text-sm text-gray-400 pb-4">
              Route starts from your location and shows the closest properties in order
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal Overlay */}
      {feedbackOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm">
          <div className="relative w-full max-w-md mx-2">
            <button
              className="absolute top-4 right-4 z-10 bg-gray-700 hover:bg-gray-600 text-white rounded-full p-2 shadow-lg"
              onClick={() => setFeedbackOpen(false)}
              aria-label="Close feedback form"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <FeedbackForm onClose={() => setFeedbackOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
